import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useApi } from "../lib/useApi";
import { useAuth } from "../lib/auth";
import { AzureUnavailable, Card, EmptyState, ErrorState, PageHeader, Spinner, Toggle } from "../components/ui";

interface FlagProject {
  id: string | number;
  name?: string;
  features?: Record<string, boolean>;
  [key: string]: unknown;
}

export default function FeatureFlags() {
  const { hasRole } = useAuth();
  const canWrite = hasRole("admin");
  const { data, loading, error, errorStatus, reload } = useApi<{ projects: FlagProject[] }>("/feature-flags");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  async function toggle(projectId: string | number, key: string, value: boolean) {
    setBusy(true);
    setActionError(null);
    try {
      await api.patch(`/feature-flags/${projectId}`, { [key]: value });
      reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <Spinner />;
  if (error) {
    return (
      <div>
        <PageHeader title="Feature flags" subtitle="project_features across paid platform projects" />
        {errorStatus === 502 || errorStatus === 503 ? <AzureUnavailable detail={error} /> : <ErrorState message={error} onRetry={reload} />}
      </div>
    );
  }

  const projects = (data?.projects ?? []).filter((p) => p.features && Object.keys(p.features).length > 0);

  return (
    <div>
      <PageHeader title="Feature flags" subtitle="project_features across paid platform projects" />
      {actionError && <ErrorState message={actionError} />}
      {projects.length === 0 ? (
        <EmptyState message="No projects with feature flags." />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {projects.map((p) => (
            <Card
              key={String(p.id)}
              title={
                <Link to={`/projects/${p.id}`} className="hover:underline">
                  {p.name ?? String(p.id)}
                </Link>
              }
            >
              <div className="space-y-2">
                {Object.entries(p.features ?? {}).map(([key, enabled]) => (
                  <div key={key} className="flex items-center justify-between text-sm">
                    <span className="font-mono text-xs text-slate-700">{key}</span>
                    <Toggle checked={Boolean(enabled)} disabled={!canWrite || busy} onChange={(v) => toggle(p.id, key, v)} />
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
